import React, { useEffect, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';

const emptyForm = {
  title: '',
  description: '',
  bodyMarkdown: ''
};

export const ProjectSkillModal = ({
  open,
  skill = null,
  saving = false,
  error = '',
  onClose,
  onSave
}) => {
  const { t } = useI18n();
  const [form, setForm] = useState(emptyForm);
  const [titleError, setTitleError] = useState('');
  const [bodyError, setBodyError] = useState('');
  const isEdit = Boolean(skill?.id);

  useEffect(() => {
    if (!open) {
      setForm(emptyForm);
      setTitleError('');
      setBodyError('');
      return;
    }

    setForm({
      title: skill?.title || '',
      description: skill?.description || '',
      bodyMarkdown: skill?.bodyMarkdown || ''
    });
    setTitleError('');
    setBodyError('');
  }, [open, skill]);

  const handleClose = () => {
    if (saving) {
      return;
    }

    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const title = form.title.trim();
    const bodyMarkdown = form.bodyMarkdown.trim();
    let hasError = false;

    if (!title) {
      setTitleError(t('skillsTitleRequired'));
      hasError = true;
    } else {
      setTitleError('');
    }

    if (!bodyMarkdown) {
      setBodyError(t('skillsBodyRequired'));
      hasError = true;
    } else {
      setBodyError('');
    }

    if (hasError || saving) {
      return;
    }

    await onSave({
      title,
      description: form.description.trim(),
      bodyMarkdown
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeDisabled={saving}
      title={isEdit ? t('skillsEditTitle') : t('skillsCreateTitle')}
      panelClassName="flex max-h-[min(90vh,720px)] max-w-2xl flex-col overflow-hidden p-0"
      contentClassName="mb-0 shrink-0 px-3 pb-0 pt-3"
    >
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          <Input
            label={t('skillsTitleField')}
            value={form.title}
            disabled={saving}
            required
            maxLength={200}
            onChange={(event) => {
              setForm({ ...form, title: event.target.value });
              if (titleError) {
                setTitleError('');
              }
            }}
          />
          {titleError ? (
            <p className="text-xs text-rose-600 dark:text-rose-300" role="alert">
              {titleError}
            </p>
          ) : null}
          <Input
            label={t('skillsDescriptionField')}
            value={form.description}
            disabled={saving}
            maxLength={1000}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />
          <Textarea
            label={t('skillsBodyField')}
            value={form.bodyMarkdown}
            disabled={saving}
            required
            className="min-h-[220px] font-mono text-xs"
            placeholder={t('skillsBodyPlaceholder')}
            onChange={(event) => {
              setForm({ ...form, bodyMarkdown: event.target.value });
              if (bodyError) {
                setBodyError('');
              }
            }}
          />
          {bodyError ? (
            <p className="text-xs text-rose-600 dark:text-rose-300" role="alert">
              {bodyError}
            </p>
          ) : null}
          <p className="text-xs leading-5 text-muted">{t('skillsBodyHint')}</p>
          {error ? (
            <p
              className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200"
              role="alert"
            >
              {error}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t border-border/70 px-3 py-2">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <LoaderCircle size={16} className="animate-spin" />
                {t('skillsSaving')}
              </>
            ) : (
              t('save')
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
